'use client'

import { Text } from '@components/primitives/Text'
import { faStethoscope } from '@fortawesome/pro-regular-svg-icons/faStethoscope'
import { algoliaReadClient } from '@lib/database/algoliaPublicClient'
import { getAlgoliaSearchIndex } from '@lib/database/getAlgoliaSearchIndex'
import { NO_CONDITIONS_VALUE } from '@lib/questionnaire/trialApplication/generatePrescreeningQuestionConfig'
import toSentence from '@lib/utilities/text/toSentence'
import { isNotEmptyOrSkippedAnswer } from '@modules/patientApplyProfile/helpers/PatientApplyProfileData'
import type { ConceptsByFrequency } from '@modules/trials/helpers/extractEntitiesFromEligibilityCriteria/types'
import uniqBy from 'lodash/uniqBy'
import { useState } from 'react'
import { InstantSearch } from 'react-instantsearch'
import ConditionSearch from '../ConditionSearch'
import { getCommonComorbidities } from './getCommonComorbidities'
import { PopularItems } from './PopularItems.client'
import { SelectedItems } from './SelectedItems.client'
import type { ConditionCollectElementProps } from './types'

const MAX_DISQUALIFYING_CONDITIONS = 5

export default function CollectComorbidities({
  disqualifyingConditions,
  nctId,
  patientProfileData,
}: ConditionCollectElementProps) {
  const conditionApplyingTo = patientProfileData.conditions
    .filter((condition) => condition !== NO_CONDITIONS_VALUE)
    .map((condition) => condition.toLowerCase())

  const commonComorbidityNames = getCommonComorbidities({ nctId })
  const uniqueComorbidities = uniqBy(
    disqualifyingConditions
      .slice(0, MAX_DISQUALIFYING_CONDITIONS)
      .concat(commonComorbidityNames),
    (condition) => condition.toLowerCase(),
  )
  const comorbiditiesToDisplay: ConceptsByFrequency = uniqueComorbidities
    .filter((name) => !conditionApplyingTo.includes(name.toLowerCase()))
    .map((name, index) => ({
      frequency: 1,
      id: index,
      name,
    }))

  const [selectedConditions, setSelectedConditions] = useState(
    patientProfileData.comorbidities.filter(isNotEmptyOrSkippedAnswer),
  )

  const handleSelectedConditions = (condition: string) => {
    setSelectedConditions((selectedConditions) => [
      ...new Set([...selectedConditions, condition]),
    ])
  }

  return (
    <div className='flex w-full flex-col gap-2'>
      <Text
        className='text-neutral900'
        element='p'
        styleName='h3'
        value={`What other conditions have you been diagnosed with other than ${conditionApplyingTo.length > 0 ? toSentence(conditionApplyingTo) : 'your primary condition'}?`}
      />
      <Text
        className='text-neutral900'
        element='p'
        styleName='p-small'
        value='You can add multiple answers'
      />

      <InstantSearch
        indexName={getAlgoliaSearchIndex('trials-by-site-locations')}
        searchClient={algoliaReadClient}
      >
        <ConditionSearch
          attribute='conditions'
          autoFocus
          highlightColor='text-primary600'
          inputClassName='rounded-lg text-sm bg-white border-2 border-neutral100 h-12 focus:border-blue500 focus:border-2'
          onSelect={handleSelectedConditions}
          placeholder='Condition'
        />
      </InstantSearch>

      <SelectedItems
        icon={faStethoscope}
        onChange={setSelectedConditions}
        title='Your conditions'
        trackingType='comorbidity'
        value={selectedConditions}
      />

      {selectedConditions.filter(Boolean).map((condition) => (
        <input
          key={condition}
          name={'comorbidities'}
          type='hidden'
          value={condition}
        />
      ))}

      <PopularItems
        addNoneOption
        itemsByFrequency={comorbiditiesToDisplay}
        onChange={setSelectedConditions}
        title='Common Related Conditions'
        trackingType='comorbidity'
        value={selectedConditions}
      />
    </div>
  )
}
